import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { NewsService } from "./news.service";
import { NewsViewModel } from "../../shared/models/news.model";
import { CoreLogService } from "../../services/log.service";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { Paging } from "../../shared/paging";
import Utils from "../../shared/utils";
import { Location } from "@angular/common";
import { ScrollHelper } from "../../shared/scrollHelper";
import { Router } from "@angular/router";
import { RatingService } from "../../services/rating.service";
import { LikeService } from "../../services/like.service";
import { ContentType, SortDirection, SnackBarMessageType } from "../../shared/contentTypes";
import { SortOption } from "../../shared/models/sort.model";
import { PagingHelper } from "../../shared/pagingHelper";
import { SnackBarService, snackBarMessageKey } from "../../services/snackBar.service";
import { LocationViewModel, NavigationTitleViewModel } from "../../shared/models";
import { RouterHelperService } from "../../services/routerHelper.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { RatingDialogComponent } from "../../modalDialogs/ratingdialog/ratingDialog.component";
import { FilterLocationDialogComponent } from "../../modalDialogs/filterLocationDialog/filterLocationDialog.component";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { trigger, transition, query, style, stagger, animate } from "@angular/animations";

@Component({
  selector: "news",
  templateUrl: "./news.component.html",
  styleUrls: ["./news.component.scss"],
  providers: [NewsService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("newsButtonEntering", [
      transition("false => true", [
        query(".news-button", [
          style({ transform: "scale(0)" }),
          stagger(100, [
            animate("400ms ease-out", style({ transform: "scale(1.1)" })),
            animate("100ms ease-in-out", style({ transform: "scale(1)" }))
          ])
        ])
      ])
    ]),
  ]
})
export class NewsComponent implements OnInit, OnDestroy {
  private static newsLocationFilterFunction(locationFilterIds: number[]): ((item: NewsViewModel) => boolean) {
    return (news: NewsViewModel): boolean => {
      return (news.locations.some(l => locationFilterIds.includes(l)));
    };
  }

  private static newsLikeFilterFunction(): ((item: NewsViewModel) => boolean) {
    return (news: NewsViewModel): boolean => {
      return (news.like);
    };
  }

  public hasData: boolean = true; // check if there is data for this page;
  public pageTitle: NavigationTitleViewModel = new NavigationTitleViewModel(); // Used as a title at the header of the page
  // if there is a targetScrollId page must be scrolled to top to show this item at the top of the page
  private targetScrollId: number = 0;
  public newsList: NewsViewModel[];
  public paging: Paging<NewsViewModel>;
  private pagingHelper: PagingHelper;
  private sortOption: SortOption[];
  public locationFilterIds: number[];
  private filterFunction: ((item: NewsViewModel) => boolean)[];
  public isSmallView: boolean = false;
  public isLoaded: boolean = false;
  private _viewTypeSubscription: Subscription;
  private _hasLikeFilter: boolean;

  public filterLocations: LocationViewModel[];
  private langSubscriber: Subscription;
  private infiniteScrollSubscriber: Subscription;
  private newsChangesSubscriber: Subscription;
  private ratingChangesSubscriber: Subscription;
  public contentType = ContentType;

  constructor(private newsService: NewsService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreLogService: CoreLogService,
    private _ratingService: RatingService,
    private _likeService: LikeService,
    private _activatedRoute: ActivatedRoute,
    private internationalisationService: InternationalisationService,
    private _routerHelperService: RouterHelperService,
    private _snackBarService: SnackBarService,
    private _router: Router,
    private _overlaySpinnerService: OverlaySpinnerService,
    private _scrollHelper: ScrollHelper,
    private _bottomSheetHelperService: BottomSheetHelperService,
    private _mainnavService: MainnavService) {

  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("NewsComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  public ngOnInit(): void {
    const me = this;
    me.newsList = [];
    me.pagingHelper = new PagingHelper();
    me.sortOption = [];
    me.filterFunction = [];
    me.locationFilterIds = [];

    me.getRouteData();

    me.langSubscriber = this.internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    // reload news data if data has been changed in storage.
    me.newsChangesSubscriber = me.newsService.ObserveChanges.subscribe((changed) => {
      me.ReloadDataAndRender();
    });

    me.onViewTypeChanges(me._mainnavService.viewType);
    me._viewTypeSubscription = me._mainnavService.observeViewTypeChanges.subscribe((viewType) => {
      me.onViewTypeChanges(viewType);
      me.PushChanges();
    });

    me.initializeSortAndFilterOption();
    me.setInfiniteScrollSubscriber();
    me.setRatingSubscriber();
    me.initializeData();
    me.PushChanges();
  }

  private onViewTypeChanges(viewType: ViewType) {
    const me = this;
    me.isSmallView = (viewType === ViewType.Small);
  }

  /**set sorting option and filter at the first loading of the page */
  private initializeSortAndFilterOption() {
    const me = this;
    me.sortOption.push(me.pagingHelper.createSortOption("momentDate", SortDirection.DESC));
  }

  // set subscriber for scrollbar and load data for next page when it is in the exact point.
  private setInfiniteScrollSubscriber() {
    const me = this;
    me.infiniteScrollSubscriber = me._scrollHelper.InfiniteScrollObserver().subscribe((loadNextPage) => {
      if (loadNextPage && !Utils.IsNull(me.paging)) {
        me.newsList.push(...me.paging.nextPage());
        me.PushChanges();

      }
    });
  }

  /**
   * Set the title of the page by title Data in route data
   * This data has been set in RouterHelperService by each navigation End Event
   */
  private getRouteData() {
    const me = this;
    me.pageTitle = me._activatedRoute.snapshot.data["pageTitle"];
    me._hasLikeFilter = me._activatedRoute.snapshot.data["hasLikeFilter"];
    const routeTargetData = me._activatedRoute.snapshot.data["routeTargetData"];
    if (routeTargetData && routeTargetData["targetScrollId"]) {
      me.targetScrollId = routeTargetData["targetScrollId"];
    }

  }

  /**add user's likes to filterFunction base on user's likes */
  private async initializeData() {
    const me = this;
    try {
      const locationIDs = await me._likeService.GetContentIDForActiveLikes(ContentType.Location);
      if (locationIDs && locationIDs.length > 0) {
        me.locationFilterIds = locationIDs;
        me.filterFunction.push(NewsComponent.newsLocationFilterFunction(me.locationFilterIds));
      }
    } catch (e) {
      me._coreLogService.error("NewsComponent.initializeData", "GetContentIDForActiveLikes", e);
    }
    me.ReloadDataAndRender();
  }

  private setRatingSubscriber() {
    const me = this;
    me.ratingChangesSubscriber = me._ratingService.ObserveChanges.subscribe((changedRatings) => {
      // check, is one of the rating a news key, then reload the view, with new data
      let newsRatingChanged = false;
      const newsKeyPrefix = ContentType.News + "|";
      for (const changedRating of changedRatings) {
        if (changedRating.toString().startsWith(newsKeyPrefix)) {
          newsRatingChanged = true;
          break;
        }
      }
      if (newsRatingChanged) {
        me.ReloadDataAndRender();
      }
    });
  }

  /** get news data from storage
   */
  private ReloadDataAndRender(): void {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me.newsService.GetNewsViewModel()
      .then(news => {
        if (news) {
          if (me._hasLikeFilter) {
            news = me._likeService.filterByLike(news);
          }
          const DefaultPageSize = 4;
          let CurrentPage = 2;
          if (!Utils.IsNull(me.paging)) {
            CurrentPage = me.paging.CurrentPage;
          }
          me.paging = new Paging(news, DefaultPageSize, me.sortOption, me.filterFunction);
          me.newsList = []; // clean up
          const result = me.paging.initLoad(CurrentPage);
          // if there is a targetScrollId, the page must be scrolled to top to show this item at the top of the page
          if (me.targetScrollId > 0) {
            // call next page until a viewModel with targetScrollId is found
            let foundScrollTarget = false;
            let searchedAllData = false;
            let lastAddedPage: NewsViewModel[] = result;
            while (!foundScrollTarget && !searchedAllData) {
              foundScrollTarget = lastAddedPage.some(s => s.id === me.targetScrollId);
              searchedAllData = result.length === me.paging.getAllData().length;
              if (!foundScrollTarget) {
                lastAddedPage = me.paging.nextPage();
                result.push(...lastAddedPage);
              }
            }
            if (foundScrollTarget) {
              setTimeout(() => {
                const ele = document.getElementById("scrollTargetId-" + me.targetScrollId);
                me._scrollHelper.ScrollElementToTop(ele.offsetTop);
                // avoid auto scrolling to target again
                me.targetScrollId = 0;
              }, 200);
            } else {
              me._coreLogService.error("NewsComponent.ReloadDataAndRender", "Tried to scroll to a non existing target", me.targetScrollId);
            }
          }
          me.isLoaded = true;
          me.newsList.push(...result);
          me.hasData = me.newsList.length > 0;
          me.PushChanges();
        } else {
          me.hasData = false;
          me.PushChanges();
        }
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch(e => {
        me._overlaySpinnerService.HideLayoutSpinner();
        me._coreLogService.error("NewsComponent.ReloadDataAndRender", "GetNewsViewModel", e);
        me._snackBarService.ShowErrorMessageFailed(71);
      });
  }

  /**
   * Open a ModalDialog for rating a news or watching news rating info
   * @param news is empty if there is no rating yet. sending item Id and contentType to show rating data in modalDialog if user rate this item before.
   */
  public openRateModel(news: NewsViewModel): void {
    const me = this;
    const ratingModel = me._bottomSheetHelperService.open(RatingDialogComponent, {
      data: { contentId: news.id, contentType: ContentType.News, title: news.label },
    });
    // show snackBar after rating Item
    ratingModel.afterDismissed().subscribe((result: any) => {
      if (result) {
        result
          .then(() => {
            me._snackBarService.ShowMessage(snackBarMessageKey.successfullyExecuted, SnackBarMessageType.Successful);
          })
          .catch(e => {
            me._coreLogService.error("NewsComponent.openRateModel", "afterClosed", e);
            me._snackBarService.ShowErrorMessageFailed(72);
          });
      }
    });
  }

  /**
   * Open a ModalDialog for rating a news or watching news rating info
   */
  public openFilterDialog() {
    const me = this;
    const bottomSheetRef = me._bottomSheetHelperService.open(FilterLocationDialogComponent, {
      data: me.locationFilterIds
    });
    // show snackBar after rating Item
    bottomSheetRef.afterDismissed().subscribe((result: number[]) => {
      if (result) {
        me.filterFunction = [];
        me.locationFilterIds = result;
        if (me.locationFilterIds && me.locationFilterIds.length > 0) {
          me.filterFunction.push(NewsComponent.newsLocationFilterFunction(me.locationFilterIds));
        }
        me.paging.setFilter(me.filterFunction);
        me.paging.setSorting(me.sortOption);
        me.newsList = []; // clean up
        me.newsList.push(...me.paging.initLoad());
        me.hasData = me.newsList.length > 0;
        me.PushChanges();
      }
    });
  }

  public navigateBack() {
    this._routerHelperService.navigateBackSimple();
  }

  public navigateToNewsDetail(newsId: number | string): void {
    this._router.navigate(["/newsdetail", newsId]);
  }

  ngOnDestroy(): void {
    const me = this;
    if (me.langSubscriber) {
      me.langSubscriber.unsubscribe();
    }
    if (me.infiniteScrollSubscriber) {
      me.infiniteScrollSubscriber.unsubscribe();
    }
    if (me.newsChangesSubscriber) {
      me.newsChangesSubscriber.unsubscribe();
    }
    if (me.ratingChangesSubscriber) {
      me.ratingChangesSubscriber.unsubscribe();
    }
  }
}
